// ORL blendshape + linear-blend-skinning deform — the browser's CPU path, and
// the reference the other three implementations are held to
// (src/lib/orl/wgsl/orl_deform.wgsl, server/orl/deform.py and the torch kernel
// wrapping it). They are SIBLINGS, not ports of each other: each exists because
// its runtime cannot host the others, and `tests/unit/orlDeformSynthetic.test.mjs`
// plus `tests/python/test_orl_deform.py` pin them to one committed set of numbers
// so a change here cannot land alone.
//
// NO three.js import, so the node gates can exercise the exact code that ships.
//
// Inputs are the baked bundle buffers (the members of the character's
// `orl_pack.bin`) plus per-frame ORL outputs (getJointOutputs /
// getBlendShapeOutputs). Output: Float32Array[V*3] in
// centimetres (same units as the bundle / DNA). The caller scales to metres at
// the render boundary.
//
// Math (mirrors the reference exactly):
//   verts = neutral + Σ_ch bsOut[ch] · delta[ch]                      (blendshapes)
//   local_j = T(t_n+t_d) · R_xyz(r_n) · R_xyz(r_d) · S(1+s_d)         (posed local)
//   world_j = (parent==j) ? local_j : world_parent · local_j          (forward walk)
//   skin_j  = world_j · inverseBind_j
//   v'      = Σ_i w_i · skin_{joint_i} · [v,1]                         (LBS)
// Rotations: Maya extrinsic 'xyz' in degrees -> R = Rz·Ry·Rx (column-vector).
// Matrices are flat row-major Float64Array(16) to match numpy indexing.

// Maya extrinsic xyz euler (degrees) -> 3x3 = Rz·Ry·Rx, written into the top-left
// of a row-major 4x4 already holding translation/homogeneous row.
function deg2mat3(rx, ry, rz, out) {
  const d = Math.PI / 180;
  const cx = Math.cos(rx * d), sx = Math.sin(rx * d);
  const cy = Math.cos(ry * d), sy = Math.sin(ry * d);
  const cz = Math.cos(rz * d), sz = Math.sin(rz * d);
  // Ry·Rx first (call it A), then Rz·A.
  // Rx = [[1,0,0],[0,cx,-sx],[0,sx,cx]]
  // Ry = [[cy,0,sy],[0,1,0],[-sy,0,cy]]
  // A = Ry·Rx
  const a00 = cy,          a01 = sy * sx,        a02 = sy * cx;
  const a10 = 0,           a11 = cx,             a12 = -sx;
  const a20 = -sy,         a21 = cy * sx,        a22 = cy * cx;
  // Rz = [[cz,-sz,0],[sz,cz,0],[0,0,1]]; R = Rz·A
  out[0] = cz * a00 - sz * a10; out[1] = cz * a01 - sz * a11; out[2] = cz * a02 - sz * a12;
  out[4] = sz * a00 + cz * a10; out[5] = sz * a01 + cz * a11; out[6] = sz * a02 + cz * a12;
  out[8] = a20;                 out[9] = a21;                 out[10] = a22;
}

// C = A · B  (all flat row-major 4x4).
function mat4mul(A, B, C) {
  for (let i = 0; i < 4; i++) {
    const ai = i * 4;
    const a0 = A[ai], a1 = A[ai + 1], a2 = A[ai + 2], a3 = A[ai + 3];
    C[ai]     = a0 * B[0]  + a1 * B[4]  + a2 * B[8]  + a3 * B[12];
    C[ai + 1] = a0 * B[1]  + a1 * B[5]  + a2 * B[9]  + a3 * B[13];
    C[ai + 2] = a0 * B[2]  + a1 * B[6]  + a2 * B[10] + a3 * B[14];
    C[ai + 3] = a0 * B[3]  + a1 * B[7]  + a2 * B[11] + a3 * B[15];
  }
}

/**
 * Build a deformer from already-fetched bundle data.
 * @param {object} manifest  parsed manifest.json
 * @param {object} bufs      { neutral_pos, joint_neutral, joint_parents, inverse_bind,
 *                             skin_idx, skin_w, bs_index, bs_delta } as ArrayBuffers
 * @param {Array}  bsTargets parsed bs_targets.json  [{channel, offset, count}]
 */
export function createDeformer(manifest, bufs, bsTargets) {
  const V = manifest.V;
  const J = manifest.numJoints;
  const maxInf = manifest.maxInfluence;

  const neutral = new Float32Array(bufs.neutral_pos);          // [V*3] cm
  const jointNeutral = new Float32Array(bufs.joint_neutral);    // [J*9]
  const parents = new Int32Array(bufs.joint_parents);           // [J]

  // The hierarchy must be ROOTED and TOPOLOGICALLY ORDERED. computeSkin walks
  // joints once, front to back, reading world[parent] before writing world[j],
  // and `world` is scratch REUSED ACROSS FRAMES — so a parent that comes after
  // its child reads LAST FRAME's matrix for it, propagated to every descendant:
  // no exception, no NaN, just a face posed off a stale parent. An index past J
  // reads undefined and poisons the pose with NaN instead. The bake refuses such
  // a DNA (bakeFromDna.js) but a pack arrives from the asset manager and was not
  // necessarily made by it, so check it where the assumption is used. Mirrors
  // _check_parents in server/orl/deform.py.
  if (parents.length !== J) {
    throw new Error(`[orl] joint_parents has ${parents.length} entries, expected ${J}`);
  }
  for (let j = 0; j < J; j++) {
    const p = parents[j];
    if (p < 0 || p >= J) {
      throw new Error(`[orl] joint ${j} has parent ${p}, outside [0, ${J})`);
    }
    if (p > j) {
      throw new Error(
        `[orl] joint hierarchy is not topologically ordered: joint ${j} has parent ${p}, ` +
          `which comes after it — the forward walk would read a stale matrix for it and every descendant`,
      );
    }
  }
  const invBind = new Float32Array(bufs.inverse_bind);          // [J*16] row-major
  const skinIdx = new Uint16Array(bufs.skin_idx);               // [V*maxInf]  (quantized u16)
  const bsIndex = new Uint16Array(bufs.bs_index);               // [sumCount]  (quantized u16)
  const skinW = new Float32Array(bufs.skin_w);                  // [V*maxInf]  (fp32: LBS-sensitive)

  // Blendshape deltas ship as int8 with a per-target scale -> dequant to fp32
  // once at load (keeps the per-frame loop identical to the float32 path).
  const bsQ = new Int8Array(bufs.bs_delta);                     // [sumCount*3]
  const bsDelta = new Float32Array(bsQ.length);
  // The runs must TILE bsQ — contiguous, no gap, no overlap. Checked rather than
  // assumed: a gap leaves bsDelta at zero there, so those deltas silently vanish
  // from every posed frame, which is not a crash and not visible in a log. The
  // bake writes them contiguously (bakeFromDna.js), but a pack arrives from the
  // asset manager and was not necessarily made by it. Mirrors server/orl/deform.py.
  let covered = 0;
  for (let t = 0; t < bsTargets.length; t++) {
    const s = bsTargets[t].scale || 0;
    if (bsTargets[t].offset !== covered) {
      throw new Error(
        `[orl] bs_targets do not tile bs_delta: target for channel ${bsTargets[t].channel} ` +
          `starts at ${bsTargets[t].offset}, expected ${covered}`,
      );
    }
    const start = bsTargets[t].offset * 3, end = start + bsTargets[t].count * 3;
    for (let i = start; i < end; i++) bsDelta[i] = bsQ[i] * s;
    covered = bsTargets[t].offset + bsTargets[t].count;
  }
  if (covered * 3 !== bsQ.length) {
    throw new Error(
      `[orl] bs_targets cover ${covered} of ${bsQ.length / 3} blendshape entries — the tail would stay zero`,
    );
  }

  // Scratch reused across frames.
  const verts = new Float32Array(V * 3);       // blendshaped neutral (cm)
  const world = new Float64Array(J * 16);      // posed world matrices
  const skin = new Float64Array(J * 16);       // world · invBind
  const out = new Float32Array(V * 3);         // final posed verts (cm)
  const L = new Float64Array(16);              // per-joint local scratch
  const R = new Float64Array(16);              // per-joint rotation scratch
  L[15] = 1; R[15] = 1;

  /**
   * @param {Float32Array} jointOut  ORL getJointOutputs() [J*9] deltas
   * @param {Float32Array} bsOut     ORL getBlendShapeOutputs() [numChannels]
   * @returns {Float32Array} posed vertices [V*3] in cm (internal scratch — copy if retained)
   */
  /** Steps 2+3 alone: posed world matrices -> skin = world @ invBind, into the
   *  `skin` scratch (row-major 4x4 per joint). Split out of deform() so the GPU
   *  deform path reuses the IDENTICAL matrices — calibrate() derives `corr` from
   *  the CPU deform, so any divergence becomes a constant error in every pose. */
  function computeSkin(jointOut) {

    // 2) posed world matrices (forward walk; parents topologically ordered)
    for (let j = 0; j < J; j++) {
      const jn = j * 9, jo = j * 9;
      const tnx = jointNeutral[jn],     tny = jointNeutral[jn + 1], tnz = jointNeutral[jn + 2];
      const rnx = jointNeutral[jn + 3], rny = jointNeutral[jn + 4], rnz = jointNeutral[jn + 5];
      const tdx = jointOut[jo],         tdy = jointOut[jo + 1],     tdz = jointOut[jo + 2];
      const rdx = jointOut[jo + 3],     rdy = jointOut[jo + 4],     rdz = jointOut[jo + 5];
      const sdx = jointOut[jo + 6],     sdy = jointOut[jo + 7],     sdz = jointOut[jo + 8];

      // L = R_xyz(r_n) · R_xyz(r_d) · S(1+s_d)   (upper-left 3x3), then translation.
      deg2mat3(rnx, rny, rnz, L);            // L3 = R(r_n)
      deg2mat3(rdx, rdy, rdz, R);            // R3 = R(r_d)
      // tmp3 = L3 · R3 -> write into L3 via a small local (reuse R as scratch after read)
      const l00 = L[0], l01 = L[1], l02 = L[2];
      const l10 = L[4], l11 = L[5], l12 = L[6];
      const l20 = L[8], l21 = L[9], l22 = L[10];
      const r00 = R[0], r01 = R[1], r02 = R[2];
      const r10 = R[4], r11 = R[5], r12 = R[6];
      const r20 = R[8], r21 = R[9], r22 = R[10];
      // m = L3 · R3, then columns scaled by S(1+s_d)
      const sx = 1 + sdx, sy = 1 + sdy, sz = 1 + sdz;
      L[0] = (l00 * r00 + l01 * r10 + l02 * r20) * sx;
      L[1] = (l00 * r01 + l01 * r11 + l02 * r21) * sy;
      L[2] = (l00 * r02 + l01 * r12 + l02 * r22) * sz;
      L[4] = (l10 * r00 + l11 * r10 + l12 * r20) * sx;
      L[5] = (l10 * r01 + l11 * r11 + l12 * r21) * sy;
      L[6] = (l10 * r02 + l11 * r12 + l12 * r22) * sz;
      L[8] = (l20 * r00 + l21 * r10 + l22 * r20) * sx;
      L[9] = (l20 * r01 + l21 * r11 + l22 * r21) * sy;
      L[10] = (l20 * r02 + l21 * r12 + l22 * r22) * sz;
      L[3]  = tnx + tdx;
      L[7]  = tny + tdy;
      L[11] = tnz + tdz;
      L[12] = 0; L[13] = 0; L[14] = 0; L[15] = 1;

      const wj = j * 16;
      const p = parents[j];
      if (p === j) {
        for (let e = 0; e < 16; e++) world[wj + e] = L[e];
      } else {
        // world[j] = world[p] · L   (both row-major)
        const wp = p * 16;
        for (let i = 0; i < 4; i++) {
          const wpi = wp + i * 4;
          const a0 = world[wpi], a1 = world[wpi + 1], a2 = world[wpi + 2], a3 = world[wpi + 3];
          world[wj + i * 4]     = a0 * L[0] + a1 * L[4] + a2 * L[8]  + a3 * L[12];
          world[wj + i * 4 + 1] = a0 * L[1] + a1 * L[5] + a2 * L[9]  + a3 * L[13];
          world[wj + i * 4 + 2] = a0 * L[2] + a1 * L[6] + a2 * L[10] + a3 * L[14];
          world[wj + i * 4 + 3] = a0 * L[3] + a1 * L[7] + a2 * L[11] + a3 * L[15];
        }
      }
    }

    // 3) skin_j = world_j · invBind_j  (invBind is Float32, multiply manually)
    for (let j = 0; j < J; j++) {
      const b = j * 16;
      for (let i = 0; i < 4; i++) {
        const wi = b + i * 4;
        const a0 = world[wi], a1 = world[wi + 1], a2 = world[wi + 2], a3 = world[wi + 3];
        skin[b + i * 4]     = a0 * invBind[b]      + a1 * invBind[b + 4]  + a2 * invBind[b + 8]  + a3 * invBind[b + 12];
        skin[b + i * 4 + 1] = a0 * invBind[b + 1]  + a1 * invBind[b + 5]  + a2 * invBind[b + 9]  + a3 * invBind[b + 13];
        skin[b + i * 4 + 2] = a0 * invBind[b + 2]  + a1 * invBind[b + 6]  + a2 * invBind[b + 10] + a3 * invBind[b + 14];
        skin[b + i * 4 + 3] = a0 * invBind[b + 3]  + a1 * invBind[b + 7]  + a2 * invBind[b + 11] + a3 * invBind[b + 15];
      }
    }

    return skin;
  }

  /** Full CPU deform: blendshapes onto the neutral, then LBS. */
  function deform(jointOut, bsOut) {
    // 1) blendshapes onto neutral
    verts.set(neutral);
    for (let t = 0; t < bsTargets.length; t++) {
      const w = bsOut[bsTargets[t].channel];
      if (w === 0) continue;
      const o = bsTargets[t].offset, c = bsTargets[t].count;
      for (let k = 0; k < c; k++) {
        const vi = bsIndex[o + k] * 3;
        const di = (o + k) * 3;
        verts[vi]     += w * bsDelta[di];
        verts[vi + 1] += w * bsDelta[di + 1];
        verts[vi + 2] += w * bsDelta[di + 2];
      }
    }

    computeSkin(jointOut);

    // 4) LBS
    for (let v = 0; v < V; v++) {
      const vi = v * 3;
      const x = verts[vi], y = verts[vi + 1], z = verts[vi + 2];
      let ox = 0, oy = 0, oz = 0;
      const si = v * maxInf;
      for (let k = 0; k < maxInf; k++) {
        const w = skinW[si + k];
        if (w === 0) continue;
        const b = skinIdx[si + k] * 16;
        ox += w * (skin[b]      * x + skin[b + 1]  * y + skin[b + 2]  * z + skin[b + 3]);
        oy += w * (skin[b + 4]  * x + skin[b + 5]  * y + skin[b + 6]  * z + skin[b + 7]);
        oz += w * (skin[b + 8]  * x + skin[b + 9]  * y + skin[b + 10] * z + skin[b + 11]);
      }
      out[vi] = ox; out[vi + 1] = oy; out[vi + 2] = oz;
    }
    return out;
  }

  /** Skin matrices as PACKED ROWS: 12 floats per joint (rows 0-2 of the
   *  row-major 4x4), the layout the WGSL deform reads as 3 vec4s. */
  const skinRows = new Float32Array(J * 12);
  function computeSkinRows(jointOut) {
    computeSkin(jointOut);
    for (let j = 0; j < J; j++) {
      const b = j * 16, r = j * 12;
      for (let row = 0; row < 3; row++) {
        skinRows[r + row * 4]     = skin[b + row * 4];
        skinRows[r + row * 4 + 1] = skin[b + row * 4 + 1];
        skinRows[r + row * 4 + 2] = skin[b + row * 4 + 2];
        skinRows[r + row * 4 + 3] = skin[b + row * 4 + 3];
      }
    }
    return skinRows;
  }

  // `world` is exposed as a live view, not a copy: the rigid-shell deform
  // (shellDeform.js) drives a branch the DNA does not skin — the eyes/teeth
  // mesh — from joint MOTION alone, so it needs world_j, not skin_j. Reading it
  // costs nothing and computeSkin() already builds it. Valid only immediately
  // after a computeSkin() call; it is overwritten by the next one.
  return { V, J, maxInf, neutral, deform, computeSkin, computeSkinRows, world,
           buffers: { neutral, skinIdx, skinW, bsIndex, bsDelta }, bsTargets };
}
